/**
 * SkillCreateDialog — Modal form to create a new skill with all fields
 * Used in AdminSkills page
 */

import { useState, useEffect } from 'preact/hooks';
import { T } from '../styles/tokens';
import { Button, Textarea } from './ui';

interface SkillCreateDialogProps {
  isOpen: boolean;
  isLoading?: boolean;
  error?: string;
  onClose: () => void;
  onSubmit: (skill: {
    name: string;
    description: string;
    category: string;
    emoji: string;
    skillMarkdown: string;
    tags?: string[];
    author?: string;
  }) => Promise<void>;
}

const DEFAULT_MARKDOWN = `# {name}

{description}

## Usage

- Feature 1
- Feature 2

## Requirements

- Node.js 18+`;

export function SkillCreateDialog({
  isOpen,
  isLoading = false,
  error,
  onClose,
  onSubmit,
}: SkillCreateDialogProps) {
  const [form, setForm] = useState({
    name: '',
    description: '',
    category: '',
 emoji: '',
    skillMarkdown: DEFAULT_MARKDOWN,
    tags: '',
    author: '',
  });

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }
    return () => document.body.classList.remove('modal-open');
  }, [isOpen]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isLoading && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isLoading, isOpen, onClose]);

  if (!isOpen) return null;

  async function handleSubmit(e: Event) {
    e.preventDefault();
    try {
      await onSubmit({
        ...form,
        tags: form.tags
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean),
      });
      setForm({
        name: '',
        description: '',
        category: '',
 emoji: '',
        skillMarkdown: DEFAULT_MARKDOWN,
        tags: '',
        author: '',
      });
    } catch (e: any) {
      console.error('Error submitting form:', e);
    }
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 99998,
        background: T.bgOverlay,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '20px',
        overflow: 'auto',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !isLoading) onClose();
      }}
    >
      <div
        style={{
          background: T.bgOverlay,
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderRadius: '16px',
          border: '1px solid #1e293b',
          width: '100%',
          maxWidth: '600px',
          boxShadow: `0 25px 60px ${T.blackAlpha50}`,
          padding: '32px',
          marginTop: '40px',
          marginBottom: '40px',
        }}
      >
        <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: 'var(--text)' }}>
          Criar Novo Skill
        </h2>
        <p style={{ margin: '8px 0 20px', fontSize: '13px', color: 'var(--text-muted)' }}>
          Adicione um novo skill ao sistema
        </p>

        {error && (
          <div
            style={{
              padding: '12px 16px',
              borderRadius: '8px',
              background: T.dangerBg,
              border: `1px solid ${T.dangerBgStrong}`,
              color: T.danger,
              fontSize: '12px',
              marginBottom: '16px',
            }}
          >
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Name + Emoji */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px', gap: '12px' }}>
            <div>
              <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-muted)' }}>
                Nome do Skill
              </label>
              <input
                type="text"
                value={form.name}
                onChange={(e: any) => setForm({ ...form, name: e.target.value })}
                placeholder="ex: seo-analysis"
                disabled={isLoading}
                style={{
                  marginTop: '6px',
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: '8px',
                  border: '1px solid #1e293b',
                  background: T.bgOverlay,
                  color: 'var(--text)',
                  fontSize: '13px',
                  opacity: isLoading ? 0.5 : 1,
                }}
              />
            </div>
            <div>
              <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-muted)' }}>
                Emoji
              </label>
              <input
                type="text"
                value={form.emoji}
                onChange={(e: any) => setForm({ ...form, emoji: e.target.value })}
                disabled={isLoading}
                maxLength={2}
                style={{
                  marginTop: '6px',
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: '8px',
                  border: '1px solid #1e293b',
                  background: T.bgOverlay,
                  color: 'var(--text)',
                  fontSize: '20px',
                  textAlign: 'center',
                  opacity: isLoading ? 0.5 : 1,
                }}
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-muted)' }}>
              Descrição
            </label>
            <Textarea
              value={form.description}
              onChange={(e: any) => setForm({ ...form, description: e.target.value })}
              placeholder="Descrição breve do skill"
              disabled={isLoading}
              style={{
                marginTop: '6px',
                width: '100%',
                padding: '10px 12px',
                borderRadius: '8px',
                border: '1px solid #1e293b',
                background: T.bgOverlay,
                color: 'var(--text)',
                fontSize: '13px',
                minHeight: '80px',
                fontFamily: 'inherit',
                resize: 'vertical',
                opacity: isLoading ? 0.5 : 1,
              }}
            />
          </div>

          {/* Category + Tags */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-muted)' }}>
                Categoria
              </label>
              <input
                type="text"
                value={form.category}
                onChange={(e: any) => setForm({ ...form, category: e.target.value })}
                placeholder="ex: seo"
                disabled={isLoading}
                style={{
                  marginTop: '6px',
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: '8px',
                  border: '1px solid #1e293b',
                  background: T.bgOverlay,
                  color: 'var(--text)',
                  fontSize: '13px',
                  opacity: isLoading ? 0.5 : 1,
                }}
              />
            </div>
            <div>
              <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-muted)' }}>
                Tags (separadas por vírgula)
              </label>
              <input
                type="text"
                value={form.tags}
                onChange={(e: any) => setForm({ ...form, tags: e.target.value })}
                placeholder="tag1, tag2, tag3"
                disabled={isLoading}
                style={{
                  marginTop: '6px',
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: '8px',
                  border: '1px solid #1e293b',
                  background: T.bgOverlay,
                  color: 'var(--text)',
                  fontSize: '13px',
                  opacity: isLoading ? 0.5 : 1,
                }}
              />
            </div>
          </div>

          {/* Author */}
          <div>
            <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-muted)' }}>
              Autor (opcional)
            </label>
            <input
              type="text"
              value={form.author}
              onChange={(e: any) => setForm({ ...form, author: e.target.value })}
              placeholder="Nome do autor"
              disabled={isLoading}
              style={{
                marginTop: '6px',
                width: '100%',
                padding: '10px 12px',
                borderRadius: '8px',
                border: '1px solid #1e293b',
                background: T.bgOverlay,
                color: 'var(--text)',
                fontSize: '13px',
                opacity: isLoading ? 0.5 : 1,
              }}
            />
          </div>

          {/* Skill Markdown */}
          <div>
            <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-muted)' }}>
              Conteúdo do Skill (Markdown)
            </label>
            <Textarea
              value={form.skillMarkdown}
              onChange={(e: any) => setForm({ ...form, skillMarkdown: e.target.value })}
              disabled={isLoading}
              style={{
                marginTop: '6px',
                width: '100%',
                padding: '10px 12px',
                borderRadius: '8px',
                border: '1px solid #1e293b',
                background: T.bgOverlay,
                color: 'var(--text)',
                fontSize: '12px',
                fontFamily: 'monospace',
                minHeight: '200px',
                resize: 'vertical',
                opacity: isLoading ? 0.5 : 1,
              }}
            />
          </div>

          {/* Buttons */}
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '16px' }}>
            <Button variant="ghost"
              type="button"
              onClick={onClose}
              disabled={isLoading}
              style={{
                padding: '10px 20px',
                borderRadius: '8px',
                border: '1px solid #1e293b',
                background: 'transparent',
                color: 'var(--text-muted)',
                fontSize: '13px',
                fontWeight: '600',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                transition: 'all 0.15s ease',
                opacity: isLoading ? 0.5 : 1,
              }}
              onMouseEnter={(e: any) => {
                if (!isLoading) {
                  e.currentTarget.style.background = T.bgOverlay;
                  e.currentTarget.style.color = 'var(--text)';
                }
              }}
              onMouseLeave={(e: any) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = 'var(--text-muted)';
              }}
            >
              Cancelar
            </Button>
            <Button variant="ghost"
              type="submit"
              disabled={isLoading || !form.name || !form.description || !form.category}
              style={{
                padding: '10px 20px',
                borderRadius: '8px',
                border: 'none',
                background: 'linear-gradient(135deg, #8B5CF6 0%, #6D4CFF 100%)',
                color: T.text,
                fontSize: '13px',
                fontWeight: '600',
                cursor:
                  isLoading || !form.name || !form.description || !form.category
                    ? 'not-allowed'
                    : 'pointer',
                transition: 'all 0.15s ease',
                opacity: isLoading || !form.name || !form.description || !form.category ? 0.6 : 1,
              }}
              onMouseEnter={(e: any) => {
                if (form.name && form.description && form.category && !isLoading) {
                  e.currentTarget.style.boxShadow = `0 8px 24px ${T.purpleBgStrong}`;
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }
              }}
              onMouseLeave={(e: any) => {
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
 {isLoading ? 'Criando...' : 'Criar Skill'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
